import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchNotifications, markNotificationsAsRead, deleteNotifications } from '../store/slices/notificationSlice';
import { ChevronLeft, ChevronRight, Bell, Check, Trash2, Menu } from 'lucide-react';
import SideNavigation from '../components/SideNavigation';
import { useNavigate } from 'react-router-dom';
import { logoutUser } from '../store/slices/authSlice';
import useScreenSize from '../hooks/useScreenSize';

// Format UUIDs in text to short display format (first 8 chars, uppercase)
const formatUuidsInText = (text) => {
  if (!text) return text;
  // UUID regex pattern: 8-4-4-4-12 hex characters
  const uuidPattern = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi;
  return text.replace(uuidPattern, (uuid) => uuid.split('-')[0].toUpperCase());
};

const NotificationsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items: notifications, status, currentPage, totalPages, totalItems } = useSelector(state => state.notifications);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop);

  useEffect(() => {
    dispatch(fetchNotifications({ page: currentPage }));
  }, [dispatch, currentPage]);

  useEffect(() => {
    setSidebarOpen(isDesktop);
  }, [isDesktop]);

  const handlePageChange = (newPage) => {
    dispatch(fetchNotifications({ page: newPage }));
  };

  const handleNotificationSelect = (notificationId) => {
    setSelectedNotifications(prev => 
      prev.includes(notificationId) 
        ? prev.filter(id => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const handleMarkAsRead = () => {
    if (selectedNotifications.length > 0) {
      dispatch(markNotificationsAsRead(selectedNotifications));
      setSelectedNotifications([]);
    }
  };

  const handleDeleteNotifications = async () => {
    if (selectedNotifications.length > 0) {
      await dispatch(deleteNotifications(selectedNotifications));
      setSelectedNotifications([]);
    }
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/');
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const handleSelectAll = () => {
    if (selectedNotifications.length === notifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(notifications.map(n => n.id));
    }
  };

  if (status === 'loading') {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen flex">
      <SideNavigation isOpen={sidebarOpen} onToggle={toggleSidebar} onLogout={handleLogout} />

      <main className={`flex-1 overflow-x-hidden overflow-y-auto transition-all duration-300 ${
        isMobile || isTablet ? 'ml-0' : (sidebarOpen ? 'ml-64' : 'ml-16')
      }`}>
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          {(isMobile || isTablet) && (
            <button
              onClick={toggleSidebar}
              className="mb-4 p-2 bg-white rounded-md shadow-md"
            >
              <Menu className="h-6 w-6" />
            </button>
          )}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-800 flex items-center">
              <Bell className="mr-2 h-8 w-8 text-blue-500" />
              Notifications
            </h1>
            <div className="text-sm text-gray-500">
              {totalItems} total
            </div>
          </div>

          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-center">
              <div className="flex space-x-2 mb-2 sm:mb-0">
                <button 
                  onClick={handleSelectAll}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition duration-200 flex items-center"
                >
                  {selectedNotifications.length === notifications.length ? 'Deselect All' : 'Select All'}
                </button>
                <button 
                  onClick={handleMarkAsRead}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition duration-200 flex items-center"
                  disabled={selectedNotifications.length === 0}
                >
                  <Check className="mr-2 h-4 w-4" />
                  Mark as Read
                </button>
                <button 
                  onClick={handleDeleteNotifications}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition duration-200 flex items-center"
                  disabled={selectedNotifications.length === 0}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </button>
              </div>
              <div className="text-sm text-gray-500">
                {selectedNotifications.length} selected
              </div>
            </div>

            <AnimatePresence>
              {notifications.map(notification => (
                <motion.div 
                  key={notification.id}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className={`p-4 border-b border-gray-200 hover:bg-gray-50 transition duration-200 ${notification.read ? 'bg-gray-50' : 'bg-white'}`}
                >
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      checked={selectedNotifications.includes(notification.id)}
                      onChange={() => handleNotificationSelect(notification.id)}
                      className="mr-4 h-5 w-5 text-blue-500 focus:ring-blue-400"
                    />
                    <div className="flex-grow">
                      <p className={`font-semibold ${notification.read ? 'text-gray-600' : 'text-gray-800'}`}>
                        {formatUuidsInText(notification.content)}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {new Date(notification.created_at).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Type: {notification.type}
                      </p>
                    </div>
                    {!notification.read && (
                      <span className="h-3 w-3 bg-blue-500 rounded-full"></span>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
            <button 
              onClick={() => handlePageChange(currentPage - 1)} 
              disabled={currentPage === 1}
              className="w-full sm:w-auto mb-2 sm:mb-0 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition duration-200 disabled:opacity-50 flex items-center justify-center"
            >
              <ChevronLeft className="h-5 w-5 mr-2" />
              Previous
            </button>
            <span className="text-gray-600 mb-2 sm:mb-0">
              Page {currentPage} of {totalPages}
            </span>
            <button 
              onClick={() => handlePageChange(currentPage + 1)} 
              disabled={currentPage === totalPages}
              className="w-full sm:w-auto px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition duration-200 disabled:opacity-50 flex items-center justify-center"
            >
              Next
              <ChevronRight className="h-5 w-5 ml-2" />
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NotificationsPage;
